import React, { useEffect } from 'react';
import { connect } from '../../../redux/store';
import {
  actions as appendixActions,
  selectIsAppendixOwner,
} from '../../../redux/modules/settingsAppendix';
import { UncontrolledTooltip } from 'reactstrap';
import { I18n } from '@kineticdata/react';
import { Link } from '@reach/router';
import { PageTitle } from '../../shared/PageTitle';
import {
  Card,
  CardCol,
  CardRow,
  EmptyMessage,
  ErrorMessage,
  LoadingMessage,
  TimeAgo,
  Utils,
  C,
  addToast,
  addToastAlert,
  openConfirm,
  openModalForm,
  selectServicesKappSlug,
} from '@kineticdata/bundle-common';
import {
  isActiveClass,
  getActionPath,
  getCatalogRevisionLockData,
} from '../../../utils';
import { ActionCard } from '../../actions/ActionCard';
import { ContentManager } from '../../shared/content_manager/ContentManager';
import {
  CONTENT_MANAGER_MODE,
  getStatusColor,
} from '../../shared/content_manager/helpers';
import { List, Map } from 'immutable';

const AppendixComponent = ({
  portfolioId,
  serviceId,
  appendixId,
  mode,
  // Selected appendix
  appendix,
  appendixContentReady,
  appendixError,
  appendixLoading,
  // List of all revisions for the selected appendix id
  revisions,
  // Actions for the selected portfolio
  actions,
  // All portfolios and services
  portfoliosNameMap,
  servicesNameMap,
  // Are user actions pending
  actionsPending,
  // Actions
  fetchAppendix,
  cloneAppendix,
  expireAppendix,
  deleteAppendixRevision,
  // Other props
  kappSlug,
  profile,
  isOwner,
  navigate,
  appLocation,
  isMobile,
}) => {
  // Fetch the current appendix on mount
  useEffect(
    () => {
      fetchAppendix(appendixId);
    },
    [fetchAppendix, appendixId],
  );

  const isAppendixDraft =
    !!appendix &&
    appendix.values &&
    appendix.values[C.FIELD_STATUS] === 'Draft';
  const isAppendixActive =
    !!appendix &&
    appendix.values &&
    appendix.values[C.FIELD_STATUS] === 'Active';
  const doesAppendixHaveChildren =
    !!appendix && appendix.children && appendix.children.length > 0;

  const handleCreateChangeRequest = () =>
    openModalForm({
      formSlug: C.FORM_SLUG_CATALOG_CHANGE_REQUEST,
      kappSlug: kappSlug,
      title: 'Request Change',
      values: {
        'Catalog ID': appendixId,
        'Catalog Type': 'appendix',
        'Catalog Submission ID': appendix.id,
      },
      confirmationMessage: 'Your change request has been submitted.',
      duration: 5000,
    });

  const handleDeleteAppendix = () => {
    // If there is a revision, show alert that revsision needs to first be deleted
    if (revisions.size > 0 && !isAppendixDraft) {
      addToastAlert({
        severity: 'warning',
        title: 'Cannot delete appendix with existing revisions',
        message:
          'Please delete any draft revisions or wait for pending revisions to be completed before deleting the appendix.',
      });
    }
    // Otherwise delete or expire the appendix
    else {
      openConfirm({
        title: 'Delete Appendix',
        body: (
          <div>
            <span className="d-block mb-3">
              Are you sure you want to delete this appendix?
            </span>
            <em>
              This action is immediate, does not go through an approval process,
              and cannot be undone.
            </em>
          </div>
        ),
        actionName: 'Delete',
        ok: () =>
          // If appendix is a draft, delete it, otherwise expire it
          isAppendixDraft
            ? deleteAppendixRevision({
                submissionId: appendix.id,
                cancel: doesAppendixHaveChildren,
                success: submission => {
                  navigate(`..${!!mode ? '/..' : ''}`);
                  addToast('Appendix successfully deleted');
                },
                failure: error => {
                  addToastAlert({
                    severity: 'danger',
                    title: 'Failed to delete appendix',
                    message: error.message,
                  });
                },
              })
            : expireAppendix({
                submissionId: appendix.id,
                success: submission => {
                  navigate(`..${!!mode ? '/..' : ''}`);
                  addToast('Appendix successfully deleted');
                },
                failure: error => {
                  addToastAlert({
                    severity: 'danger',
                    title: 'Failed to delete appendix',
                    message: error.message,
                  });
                },
              }),
      });
    }
  };

  const handleInactivateAppendix = () => {
    openConfirm({
      title: 'Inactivate Appendix',
      body: (
        <div>
          <span className="d-block mb-3">
            Are you sure you want to inactivate this appendix? This will remove
            the active record and{' '}
            {revisions.size > 0
              ? 'leave the current revision in its place.'
              : 'create a new revision record in draft status.'}
          </span>
          <em>
            This action is immediate, does not go through an approval process,
            and cannot be undone.
          </em>
        </div>
      ),
      actionName: 'Inactivate',
      ok: () =>
        expireAppendix({
          submissionId: appendix.id,
          clone: revisions.size === 0,
          refetchItem: true,
          success: submission => {
            navigate(!!mode ? '..' : '');
            addToast('Appendix successfully inactivated');
          },
          failure: error => {
            addToastAlert({
              severity: 'danger',
              title: 'Failed to inactivate appendix',
              message: error.message,
            });
          },
        }),
    });
  };

  return (
    <div className="page-container">
      <div className="page-panel">
        <PageTitle
          className="page-title-heading--sm"
          parts={[
            appendix && appendix.values['Name'],
            serviceId && servicesNameMap.get(serviceId),
            portfolioId && portfoliosNameMap.get(portfolioId),
            'Catalog Management',
          ]}
          suptitle="APPENDIX"
          title={appendix && appendix.values['Name']}
          breadcrumbs={[
            {
              label: (
                <span>
                  <span className="fa fa-fw fa-md fa-arrow-left text-muted" />
                  <I18n>{C.SETTINGS_LABEL}</I18n>
                </span>
              ),
              to: '/settings',
            },
            {
              label: 'Catalog Management',
              to: `${appLocation}/settings/catalog/appendices`,
            },
            portfolioId && {
              label: portfoliosNameMap.get(portfolioId) || portfolioId,
              to: `${appLocation}/settings/catalog/portfolios/${portfolioId}`,
            },
            serviceId && {
              label: servicesNameMap.get(serviceId) || serviceId,
              to: `${appLocation}/settings${
                portfolioId ? `/portfolios/${portfolioId}` : ''
              }/catalog/services/${serviceId}`,
            },
          ]}
          actions={
            !appendixError &&
            appendix && [
              {
                icon: !appendixLoading ? 'sync' : 'sync fa-spin',
                aria: 'Reload Appendix',
                onClick: () => {
                  fetchAppendix(appendixId);
                },
              },
              profile.isSMO && {
                label: 'Request Change',
                onClick: handleCreateChangeRequest,
                menu: true,
              },
              isOwner &&
                revisions.size === 0 && {
                  menu: true,
                  label: 'Start Revision',
                  onClick: () =>
                    cloneAppendix({
                      submissionId: appendix.id,
                      success: submission => {
                        navigate(`revision/${submission.id}`);
                      },
                      failure: error => {
                        addToastAlert({
                          severity: 'danger',
                          title: 'Failed to start a revision',
                          message: error.message,
                        });
                      },
                    }),
                  disabled: actionsPending,
                },
              profile.isSMO &&
                isAppendixActive && {
                  label: 'Inactivate Appendix',
                  onClick: handleInactivateAppendix,
                  className: 'text-danger',
                  disabled: actionsPending,
                  menu: true,
                  divider: true,
                },
              profile.isSMO && {
                label: 'Delete Appendix',
                onClick: handleDeleteAppendix,
                className: 'text-danger',
                disabled: actionsPending,
                menu: true,
                divider: !isAppendixActive,
              },
            ]
          }
        />

        {appendixError ? (
          <ErrorMessage
            title="Failed to retrieve appendix."
            message={appendixError.message}
          />
        ) : !appendixContentReady && appendixLoading ? (
          <LoadingMessage />
        ) : (
          !!appendix && (
            <>
              {appendix.values['Status'] !== 'Active' && (
                <div className="alert alert-info alert-bar mb-3">
                  <I18n>This is a new appendix that is not yet published.</I18n>{' '}
                  {isOwner && (
                    <I18n>
                      Go to the revision tab to continue editing this appendix.
                    </I18n>
                  )}
                </div>
              )}

              <ul className="nav nav-tabs h6" role="tablist">
                <li role="tab" className="nav-item">
                  <Link
                    to={!mode ? '.' : '..'}
                    getProps={isActiveClass('nav-link')}
                  >
                    <I18n>Appendix</I18n>
                  </Link>
                </li>
                {revisions.size > 0 && (
                  <li role="tab" className="nav-item">
                    <Link
                      to={!mode ? 'revision' : '../revision'}
                      getProps={isActiveClass('nav-link')}
                    >
                      <I18n>Revision</I18n>
                    </Link>
                  </li>
                )}
                {actions.size > 0 && (
                  <li role="tab" className="nav-item">
                    <Link
                      to={!mode ? 'actions' : '../actions'}
                      getProps={isActiveClass('nav-link')}
                    >
                      <I18n>Change Requests</I18n>
                    </Link>
                  </li>
                )}
              </ul>

              {!mode && (
                <div className="nav-tabs--content">
                  {appendix && appendixContentReady ? (
                    <ContentManager
                      submission={appendix}
                      mode={CONTENT_MANAGER_MODE.MANAGE}
                      username={profile.username}
                      indexSections={true}
                      enableSearch={true}
                    />
                  ) : (
                    <ErrorMessage title="Failed to retrieve appendix content." />
                  )}
                </div>
              )}

              {mode === 'revision' && (
                <div className="nav-tabs--content">
                  {revisions.size > 0 ? (
                    <div className="cards mb-4">
                      {revisions.map(r => {
                        const approval = List(r.children)
                          .filter(c => c.type === C.APPROVAL_FORM_TYPE)
                          .sortBy(a => a.updatedAt)
                          .reverse()
                          .first();
                        const isRevisionOwner = selectIsAppendixOwner(
                          profile,
                          r,
                        );

                        // Get lock info for revision
                        const {
                          isLocked,
                          isLockedByMe,
                        } = getCatalogRevisionLockData(r, profile.username);

                        return (
                          <Card key={r.id}>
                            <CardCol>
                              <CardRow type="title">
                                {isLocked &&
                                  !isLockedByMe && (
                                    <>
                                      <span
                                        id={`lock-${r.id}`}
                                        className="fa fa-lock fa-fw"
                                      />
                                      <UncontrolledTooltip
                                        placement="right"
                                        target={`lock-${r.id}`}
                                        delay={0}
                                        trigger="hover focus"
                                      >
                                        <I18n>
                                          This revision is currently locked by
                                          another user.
                                        </I18n>
                                      </UncontrolledTooltip>
                                    </>
                                  )}
                                <span>
                                  <I18n>Revision</I18n> {r.handle}
                                </span>
                                <div className="ml-auto">
                                  <Link className="btn btn-primary" to={r.id}>
                                    <I18n>
                                      {r.values['Status'] === 'Draft' &&
                                      isRevisionOwner
                                        ? 'Continue Editing'
                                        : 'Review Revision'}
                                    </I18n>
                                    <span className="fa fa-arrow-right fa-fw" />
                                  </Link>
                                </div>
                              </CardRow>
                              <CardRow className="text-muted" type="meta">
                                <dl className="flat align-items-center">
                                  <div>
                                    <dd>
                                      <span
                                        className={`badge badge-${getStatusColor(
                                          r.values['Status'],
                                        )} badge-stylized text-muted`}
                                      >
                                        <I18n>{r.values['Status']}</I18n>
                                      </span>
                                    </dd>
                                  </div>
                                  <div>
                                    <dt>
                                      <I18n>Created</I18n>
                                    </dt>
                                    <dd>
                                      <TimeAgo timestamp={r.createdAt} />
                                    </dd>
                                  </div>
                                  {r.coreState === 'Submitted' ? (
                                    <div>
                                      <dt>
                                        <I18n>Submitted</I18n>
                                      </dt>
                                      <dd>
                                        <TimeAgo timestamp={r.submittedAt} />
                                      </dd>
                                    </div>
                                  ) : (
                                    <div>
                                      <dt>
                                        <I18n>Updated</I18n>
                                      </dt>
                                      <dd>
                                        <TimeAgo timestamp={r.updatedAt} />
                                      </dd>
                                    </div>
                                  )}
                                </dl>
                              </CardRow>
                              {approval && (
                                <CardRow
                                  className="text-muted mt-1"
                                  type="meta"
                                >
                                  <dl className="flat align-items-center">
                                    {approval.coreState === 'Submitted' && (
                                      <div>
                                        <dt>
                                          <I18n>Approval Decision</I18n>
                                        </dt>
                                        <dd>
                                          <I18n>
                                            {approval.values['Approved']}
                                          </I18n>
                                        </dd>
                                      </div>
                                    )}
                                    {approval.values[
                                      C.FIELD_ASSIGNED_INDIVIDUAL_NAME
                                    ] && (
                                      <div>
                                        <dt>
                                          <I18n>Approvers</I18n>
                                        </dt>
                                        <dd>
                                          <I18n>
                                            {
                                              approval.values[
                                                C.FIELD_ASSIGNED_INDIVIDUAL_NAME
                                              ]
                                            }
                                          </I18n>
                                        </dd>
                                      </div>
                                    )}
                                    {approval &&
                                      approval.values[
                                        C.FIELD_ASSIGNED_TEAM_NAME
                                      ] && (
                                        <div>
                                          <dt>
                                            <I18n>Approval Teams</I18n>
                                          </dt>
                                          <dd>
                                            <I18n>
                                              {
                                                approval.values[
                                                  C.FIELD_ASSIGNED_TEAM_NAME
                                                ]
                                              }
                                            </I18n>
                                          </dd>
                                        </div>
                                      )}
                                    {approval.coreState === 'Submitted' &&
                                      approval.values['Approved'] ===
                                        'Denied' && (
                                        <div>
                                          <dt>
                                            <I18n>Reason</I18n>
                                          </dt>
                                          <dd>
                                            <I18n>
                                              {approval.values['Reason']}
                                            </I18n>
                                          </dd>
                                        </div>
                                      )}
                                  </dl>
                                </CardRow>
                              )}
                            </CardCol>
                          </Card>
                        );
                      })}
                    </div>
                  ) : (
                    <EmptyMessage title="A revision does not exist." />
                  )}
                </div>
              )}

              {mode === 'actions' && (
                <div className="nav-tabs--content">
                  {actions.size > 0 ? (
                    <div className="cards mb-4">
                      {actions.map(action => {
                        return (
                          <ActionCard
                            key={action.id}
                            submission={action}
                            path={getActionPath(appLocation, action)}
                            reload={() => {
                              fetchAppendix(appendixId);
                            }}
                            external={true}
                          />
                        );
                      })}
                    </div>
                  ) : (
                    <EmptyMessage title="There are no change requests." />
                  )}
                </div>
              )}
            </>
          )
        )}
      </div>
    </div>
  );
};

const mapStateToProps = (state, props) => {
  const appendix =
    state.settingsAppendix.current &&
    state.settingsAppendix.current.values[C.FIELD_APPENDIX_ID] ===
      props.appendixId
      ? state.settingsAppendix.current
      : null;
  const appendices =
    state.settingsAppendix.data.get(props.appendixId) || List();
  return {
    // Current appendix
    appendix,
    appendixContentReady: appendix && appendix.values['Content'],
    appendixLoading: state.settingsAppendix.currentLoading,
    appendixoError: state.settingsAppendix.currentError,
    // Revisions for this appendix
    revisions: appendices.filter(p => p.values['Status'] !== 'Active'),
    // Actions tied to this appendix that the user can access
    actions: List(
      appendix && appendix.children
        ? appendix.children.filter(
            c =>
              c.type === 'Action' &&
              (state.app.profile.isSMO ||
                c.values[C.FIELD_ASSIGNED_TEAMS].some(t =>
                  Utils.isMemberOf(state.app.profile, t),
                )),
          )
        : [],
    ),
    // All portfolios
    portfoliosNameMap: state.settingsPortfolio.dataList.reduce(
      (map, p) => map.set(p.values[C.FIELD_PORTFOLIO_ID], p.values['Name']),
      Map(),
    ),
    // All services
    servicesNameMap: state.settingsService.dataList.reduce(
      (map, p) => map.set(p.values[C.FIELD_SERVICE_ID], p.values['Name']),
      Map(),
    ),
    // Are any user actions are pending
    actionsPending: state.settingsAppendix.actionsPending > 0,
    // Other state
    kappSlug: selectServicesKappSlug(state),
    profile: state.app.profile,
    isOwner: selectIsAppendixOwner(state.app.profile),
    appLocation: state.app.location,
    layoutSize: state.app.layoutSize,
    isMobile: ['small', 'medium'].includes(state.app.layoutSize),
  };
};

const mapDispatchToProps = {
  fetchAppendix: appendixActions.fetchAppendixRequest,
  cloneAppendix: appendixActions.cloneAppendixRequest,
  expireAppendix: appendixActions.expireAppendixRequest,
  deleteAppendixRevision: appendixActions.deleteAppendixRevisionRequest,
};

export const Appendix = connect(
  mapStateToProps,
  mapDispatchToProps,
)(AppendixComponent);
